import React from 'react';
import { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useNavigate } from "react-router-dom";
import { API_URL } from '../config/API_URLS.tsx';
import { authenticatedFetch } from '../auth/RequestSigner.tsx';
import ErrorModal from './ErrorModal.tsx';
import SuccessLoginModal from "./SuccessLoginModal.tsx";

function LoginForm() {
    const [document, setUser] = useState("");
    const [password, setPassword] = useState("");
    const auth = useAuth();
    const navigate = useNavigate();
    
    // Estados para los modales
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    // Estado para el loader
    const [isLoading, setIsLoading] = useState(false);
    // Estados para el modal de recuperación de contraseña
    const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState("");
    const [isValidEmail, setIsValidEmail] = useState(false);
    // Estado para la navegación después de autenticación
    const [shouldNavigate, setShouldNavigate] = useState(false);

    // Efecto para verificar si el usuario ya está autenticado al cargar el componente
    useEffect(() => {
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        if (isAuthenticated) {
            navigate('/home');
        }
    }, [navigate]);

    // Efecto para manejar la navegación después de login exitoso
    useEffect(() => {
        if (shouldNavigate) {
            navigate('/home');
        }
    }, [shouldNavigate, navigate]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        // Activar el loader
        setIsLoading(true);
        
        try {
            const response = await authenticatedFetch(`${API_URL}/v1/login`, {
                method: 'post',
                body: JSON.stringify({document, password}),
                headers: {
                    'Content-Type': 'application/json'
                }                
            });
            if (!response.ok) {
                throw new Error('Error en la solicitud');            
            }
            
            const data = await response.json();            
            if(data){            
               auth.saveUser(data);
            }

            setModalMessage('Autenticado correctamente');
            setSuccessModalOpen(true);
            setTimeout(() => {
                setShouldNavigate(true);
            }, 1100);
        } catch (error) {
            console.error('Error:', error);

            setModalMessage('Usuario o contraseña incorrectos');
            setErrorModalOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Manejador para abrir el modal de recuperación de contraseña
    const handleForgotPassword = (e) => {
        e.preventDefault();
        setForgotPasswordModalOpen(true);
    };

    // Manejador para cerrar el modal de recuperación de contraseña
    const handleCloseRecoveryModal = () => {
        setForgotPasswordModalOpen(false);
        setRecoveryEmail("");
        setIsValidEmail(false);
    };

    // Manejador para enviar la solicitud de recuperación (por ahora solo cierra el modal)
    const handleSubmitRecovery = (e) => {
        e.preventDefault();
        // Aquí iría la lógica para enviar el correo de recuperación
        // Por ahora, solo cerramos el modal
        handleCloseRecoveryModal();
    };

    // Validación del correo electrónico
    const handleEmailChange = (e) => {
        const email = e.target.value;
        setRecoveryEmail(email);
        // Expresión regular para validar email
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        setIsValidEmail(emailRegex.test(email));
    };

    return (
        <div className="h-full mt-24 mb-24 flex items-center justify-center w-full dark:bg-gray-950 amber" data-testid="login-form-container">
            <div className="bg-white shadow-lg rounded-lg lg:px-14 px-11 w-11/12 py-8 max-w-md border-2 border-gray-300" data-testid="login-form-wrapper">
                <h1 className="text-3xl text-amber-700 font-bold text-center mb-4 dark:text-amber-00" data-testid="login-form-title">Iniciar Sesión</h1>
                <form action="#" data-testid="login-form">
                    <div className="mb-4" data-testid="login-form-document-field">
                        <label htmlFor="document" className="block text-s font-medium text-start text-black :white mb-2" data-testid="login-form-document-label">Documento de identidad</label>
                        <input type="text" 
                        id="user.document" 
                        className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 invalid:border-pink-600 invalid:border-2"
                        placeholder="1020304050" 
                        required
                        value={document}
                        onChange={(e) => setUser(e.target.value)}
                        data-testid="login-form-document-input"
                        />
                    </div>
                    <div className="mb-4" data-testid="login-form-password-field">
                        <label htmlFor="password" className="block text-s font-medium text-start text-gray-700 dark:text-black mb-2" data-testid="login-form-password-label">Contraseña</label>
                        <input type="password" id="password" className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 invalid:border-pink-600 invalid:border-2"
                            placeholder="**********"
                            required 
                            value = {password}
                            onChange={(e) => setPassword(e.target.value) }
                            data-testid="login-form-password-input"
                            />
                    </div>
                    <div className="flex items-center justify-between" data-testid="login-form-links">
                        <div className="flex items-start" data-testid="login-form-forgot-password">
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <div className="flex items-end">
                            <a href="/registro-aprendiz"  onClick={handleForgotPassword}
                                className="p-1 text-sm text-indigo-700 hover:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-testid="login-form-forgot-password-link">
                                    ¿Olvidaste tu contraseña?
                            </a>
                        </div>
                        
                            
                        </div>
                        {/* <div className="flex items-end">
                            <a href="/registro-aprendiz"
                                className="p-4 text-s text-indigo-700 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Registrarme
                            </a>
                        </div> */}
                        
                        
                    </div>
                    <div className="flex items-center justify-between ">
                    
                        <div className="flex items-end">
                            <a href="/estado-solicitud-registro"
                                className="p-1 text-sm text-indigo-700 hover:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    Ver estado de mi registro
                            </a>
                        </div>
                        
                        
                    </div>
                    <div className="flex items-end">
                      
                        <div className="flex items-start ">
                            <a href="/registro-aprendiz"
                                className="p-1 mb-3 mt-1 text-sm  font-semibold text-amber-700 hover:text-amber-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Registrarme
                            </a>
                        </div>
                        
                        
                    </div>
                    <button onClick={handleSubmit} 
                    type="submit" 
                    disabled={!password || !document || isLoading}
                    className={`w-full flex justify-center py-2 px-4 rounded-md shadow-sm text-sm font-medium
                     text-white relative overflow-hidden group
                     ${(!password || !document || isLoading) ? 'cursor-not-allowed opacity-50': ''} `}
                     data-testid="login-form-submit-button">
                        <div className="absolute inset-0 bg-gradient-to-t from-amber-400 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-amber-400 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                        <span className="z-10 relative text-gray-900 font-semibold">
                        {isLoading ? (
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Procesando...
                            </div>
                        ) : 'Ingresar'}
                        </span>
                    </button>
                </form>
            </div>

            {/* Modales de éxito/error */}
            <SuccessLoginModal
                isOpen={successModalOpen}
                message={modalMessage} 
            />
            
            <ErrorModal 
                isOpen={errorModalOpen} 
                onClose={() => setErrorModalOpen(false)} 
                message={modalMessage} 
            />

            {/* Modal de recuperación de contraseña */}
            {forgotPasswordModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                    <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-auto p-6 animate-fadeIn">
                        <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-3">Recuperar Contraseña</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-4">
                            Las instrucciones para recuperar tu contraseña llegarán a tu correo electrónico.
                        </p>
                        <form onSubmit={handleSubmitRecovery}>
                            <div className="mb-4">
                                <label htmlFor="recovery-email" className="block text-sm font-medium text-start text-gray-700 dark:text-gray-300 mb-2">
                                    Correo electrónico
                                </label>
                                <input 
                                    type="email" 
                                    id="recovery-email" 
                                    className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="ejemplo@correo.com" 
                                    value={recoveryEmail}
                                    onChange={handleEmailChange}
                                    required
                                />
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCloseRecoveryModal}
                                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={!isValidEmail}
                                    className={`flex-1 px-4 py-2 bg-amber-700 text-white rounded-md hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500 ${!isValidEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    Enviar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
export {LoginForm}